// Modules
import React, { RefObject } from "react";
// @ts-ignore
import { LocalizationContext } from 'react-localize';

// Styles
import "./ResortDropdown.sass";

// Interfaces
import {ResortCountry, State} from 'Interfaces/ResortCountry';
import Resort, {Activity} from "Interfaces/ResortInterface";

// Components
import ResortDropdownList from "./ResortDropdownList";
import ResortDropdownSelectedList from './ResortDropdownSelectedList';

type ResortDropdownState = {
    open: boolean,
    hidden: boolean,
    filter: string,
    selectedOverLimit: boolean
};

type Props = {
  resortData: ResortCountry[];
  selected: any;
  required: boolean;
  value: string;
  activityFilter: string;
  name: string;
  limit: number;
  item?: [Resort];
  onChange: (e: any) => void;
  onRemove : (propertyCode: string[]) => void;
  tabIndex? : number;
  resortsQueryParam?: string;
}

class ResortDropdown extends React.Component<Props, ResortDropdownState> {
  container: RefObject<any>;

  constructor(props:Props) {
    super(props);
    this.container = React.createRef();
    this.onSelect = this.onSelect.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.open = this.open.bind(this);
    this.closeDropdown = this.closeDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleTabOutside = this.handleTabOutside.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.state = {
      open: false,
      hidden: false,
      filter: "",
      selectedOverLimit: false,
    };
  }

  open() {
    this.setState({
      open: true
    });
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("keyup", this.handleTabOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("keyup", this.handleTabOutside);
  }

  flattenList(resortData: any) {
    let resorts: Resort[] = resortData.map((country:any) => {
      return country.states.flat();
    }).flat().map((state:any) => {
      return state.resorts.flat();
    }).flat();
    return resorts;
  }

  handleTabOutside(e: any){
    if (this.container.current && !this.container.current.contains(e.target)) {
      this.setState({
        open: false,
        filter: "",
        selectedOverLimit: false,
      });
    }
  }
  handleClickOutside(e:MouseEvent) {
    if (this.container.current && !this.container.current.contains(e.target)) {
      this.setState({
        open: false,
        filter: "",
        selectedOverLimit: false,
      });
    }
  }

  onFilter(filter: string) {
    this.setState({ filter });
  }

  isActiveFilter(activityFilter: string, activities : Activity[]): boolean{
    return !activityFilter || (activities && activities.findIndex((act) => act.name === activityFilter)  > -1);
  }

  filter(resortCountries: ResortCountry[], filterString: string, activityFilter: string) {
    let punctuation = /[^a-z\s]/g;

    filterString = filterString.toLowerCase().normalize("NFD").replace(punctuation, "");

    let copied = JSON.parse(JSON.stringify(resortCountries));
   
    let filteredCountries = copied.filter((country: ResortCountry) => {
      let filteredStates = country.states.filter((state: State) => {
        let filteredResorts = state.resorts.filter((resort: Resort) => {
          let activeFilter = this.isActiveFilter(activityFilter,  resort.activities);

          let fieldsToMatch: string[] = [
            resort.name === null ? 'name' : resort.name, //TODO: Siva => This was added because name was coming in as null and the app was crashing
            resort.country,
            resort.continent,
            resort.city,
            resort.state,
          ].map(field => field.toLowerCase().normalize("NFD").replace(punctuation, ""));

          const textFilter = fieldsToMatch.some(field => field.indexOf(filterString) !== -1);

          return activeFilter && textFilter;
        });

        state.resorts = filteredResorts;

        return filteredResorts.length > 0;
      });

      country.states = filteredStates;
      return filteredStates.length > 0;
     });

     return filteredCountries;
  }

  onSelect(selectedResorts:string[]) {
    let filteredSelect = selectedResorts.filter((resortName: string) => {
      return this.props.selected.indexOf(resortName) === -1;
    });
    let selectedOverLimit = false;

    let newSelected = [...this.props.selected];
    // only set selected if the limit has not been reached
    if (newSelected.length < this.props.limit) {
      newSelected.unshift(...filteredSelect);
    }
    else
    {
      selectedOverLimit = true;
    }

    this.setState({
      open: !selectedOverLimit,
      selectedOverLimit
    });

    this.props.onChange({
      target: {
        name: this.props.name,
        value: newSelected
      }
    });
  }

  clearFilter() {
    this.setState({
      filter: "",
      selectedOverLimit: false,
    });
  }

  closeDropdown(event: React.KeyboardEvent<HTMLDivElement>) {
    if(event.keyCode === 9 ) {

      this.setState({
        open:false,
        selectedOverLimit: false,
      });
    }
  }

  onRemove(propertyCode: string[]){
    this.setState({
      selectedOverLimit: false,
    });
    this.props.onRemove(propertyCode);
  }

  render() {
    const { localize } = this.context;
    let resortData = this.filter(this.props.resortData,
                                 this.state.filter,
                                 this.props.activityFilter);


    let selectedFlatList = this.flattenList(this.props.resortData);
    let txtClass = "counter-text ";
    if(this.props.selected.length === this.props.limit) {
      txtClass += "max-dropdown";
    }

    return (

      <React.Fragment>
        <div ref={this.container} className="resort-dropdown">
          {this.state.selectedOverLimit && (
            <p className={txtClass + ' max-selected'}>
              {localize('static.content.max-resorts-selected')}
            </p>
          )}
          <p className={txtClass}>
            ({this.props.selected.length} {localize('static.content.of')} {this.props.limit})
          </p>
          <input
            value={!this.state.open ? this.props.value || "" : this.state.filter || ""}
            onFocus={this.open}
            onChange={e => {this.onFilter(e.target.value)}}
            className="text-input"
            placeholder={localize('static.content.resort-name')}
            type="text"
            aria-label={localize('static.content.resort-name')}
            tabIndex={this.props.tabIndex}
          />

          {this.state.open && (
            <div className="dropdown">
              <ResortDropdownSelectedList
                selected={this.props.selected}
                resortList={selectedFlatList}
                onRemove={this.onRemove}
              />
              <ResortDropdownList
                resortList={resortData}
                onSelect={this.onSelect}
                onRemoval={this.onRemove}
                selected={this.props.selected}
                limit={this.props.limit}
                resortsQueryParam={this.props.resortsQueryParam}
              />
            </div>
          )}
          {this.props.required && <span className="required">*</span>}
        </div>
      </React.Fragment>

    );
  }
}

ResortDropdown.contextType = LocalizationContext;
export default ResortDropdown;
